import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  alpha,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import ReplayIcon from "@mui/icons-material/Replay";
import ReplyIcon from "@mui/icons-material/Reply";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import { showToast, WEBSERVICE } from "../../CoreDataService";
import axios from "axios";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";

const RecurringPayments = () => {
  const { btncolor } = useSelector((state) => state);
  const theme = useTheme();

  const [mainTab, setMainTab] = useState(0);
  const [subTab, setSubTab] = useState(0);

  const [stpLoader, setStpLoader] = useState(false);
  const [activeSubscriptionsList, setActiveSubscriptionsList] = useState([]);
  const [canceledSubscriptionsList, setCanceledSubscriptionsList] = useState(
    []
  );
  const [requestedSubscriptionsList, setRequestedSubscriptionsList] = useState(
    []
  );
  const [outstandingSubscriptionsList, setOutstandingSubscriptionsList] =
    useState([]);

  const [activeSubscriptionDisplay, setActiveSubscriptionDisplay] =
    useState(false);
  const [canceledSubscriptionDisplay, setCanceledSubscriptionDisplay] =
    useState(false);
  const [requestedSubscriptionDisplay, setRequestedSubscriptionDisplay] =
    useState(false);
  const [outstandingSubscriptionsDisplay, setOutstandingSubscriptionsDisplay] =
    useState(false);

  useEffect(() => {
    getRecurringPaymentDetails();
  }, []);

  const getRecurringPaymentDetails = async () => {
    setStpLoader(true);
    const jsonObj = { customerId: localStorage.getItem("uuid") };

    try {
      const response = await axios.post(
        `${WEBSERVICE}recurring/getRecurringPaymentDetails`,
        JSON.stringify(jsonObj),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      );

      setStpLoader(false);
      const res = response.data;

      if (res.statusCode !== 0) {
        showToast("error", res.message);
        return;
      }

      setActiveSubscriptionsList([]);
      setCanceledSubscriptionsList([]);
      setRequestedSubscriptionsList([]);
      setOutstandingSubscriptionsList([]);

      setActiveSubscriptionDisplay(true);
      setCanceledSubscriptionDisplay(true);
      setRequestedSubscriptionDisplay(true);
      setOutstandingSubscriptionsDisplay(true);

      if (res.recurringPaymentDetails?.length > 0) {
        res.recurringPaymentDetails.forEach((item) => {
          const row = {
            customerName: item.customerName,
            customerEmail: item.customerEmail,
            customerId: item.customerId,
            invoiceId: item.id,
            amount: item.amount,
            recurringPeriod: item.recurringPeriod,
            outstandingAmount: item.outstandingAmount,
            startDate: item.startDate,
            nextBillingCycleDate: item.nextBillingDate,
            status: item.description,
            attempt: item.attempt,
            merchantEmail: item.merchantEmail,
            merchantId: item.merchantId,
            merchantName: item.merchantName,
            requestedBy: item.requestBy,
            currencyCode: item.currencyCode,
            currencyId: item.currencyId,
            totalCycles: item.totalCycles,
            statusValue: item.status,
            createdOn: item.createdOn,
            id: item.id,
            endDate: item.endDate,
            cryptoWalletUsed: item.currencyCode,
          };

          if (item.status === 1) {
            setActiveSubscriptionsList((prev) => [...prev, row]);
            setActiveSubscriptionDisplay(true);
          } else if (item.status === 2) {
            setCanceledSubscriptionsList((prev) => [...prev, row]);
            setCanceledSubscriptionDisplay(true);
          } else if (item.status === 0) {
            setRequestedSubscriptionsList((prev) => [...prev, row] || []);
            setRequestedSubscriptionDisplay(true);
          } else {
            setOutstandingSubscriptionsList((prev) => [...prev, row]);
            setOutstandingSubscriptionsDisplay(true);
          }
        });
      } else {
        setActiveSubscriptionsList([]);
        setCanceledSubscriptionsList([]);
        setRequestedSubscriptionsList([]);
        setOutstandingSubscriptionsList([]);
      }
    } catch (error) {
      setStpLoader(false);
      showToast(
        "error",
        "Something went wrong while fetching recurring payment details."
      );
      console.error(error);
    }
  };

  const updateRecurringPayment = async (id, statusCode) => {
    const payload = {
      customerId: localStorage.getItem("uuid"),
      id: id,
      status: statusCode,
    };

    try {
      const response = await axios.post(
        `${WEBSERVICE}recurring/updateRecurringPayment`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );

      if (response.data.statusCode !== 0) {
        showToast("error", response.data.message);
      } else {
        showToast("success", response.data.message);
        setTimeout(() => {
          getRecurringPaymentDetails();
        }, 2000);
      }
    } catch (error) {
      showToast("error", "Something went wrong!");
      console.error(error);
    }
  };

  const mainTabsMeta = [
    {
      label: "Enable and Manage Recurring Payments",
      icon: <AutorenewOutlinedIcon sx={{ fontSize: 18 }} />,
    },
    {
      label: "View Payment Requested by Merchants",
      icon: <ChecklistOutlinedIcon sx={{ fontSize: 18 }} />,
    },
  ];

  const subTabsMeta = [
    { label: "Active Subscriptions" },
    { label: "Cancelled Subscriptions" },
    { label: "Outstanding Payment" },
  ];

  const renderEmptyRow = (colSpan, message) => (
    <TableRow>
      <TableCell colSpan={colSpan} sx={emptyCell}>
        <InboxOutlinedIcon sx={emptyIcon} />
        <Typography sx={emptyText}>{message}</Typography>
      </TableCell>
    </TableRow>
  );

  const activeHeaders = [
    "Merchant ID",
    "Merchant Name",
    "Invoice ID",
    "Amount",
    "Crypto Wallet Used",
    "Recurring Period",
    "Outstanding Amount",
    "Start Date",
    "Next Billing Cycle Date",
    "Status",
    "Actions",
  ];
  const cancelledHeaders = [
    "Merchant ID",
    "Merchant Name",
    "Invoice ID",
    "Amount",
    "Crypto Wallet Used",
    "Recurring Period",
    "Outstanding Amount",
    "Start Date",
    "End Date",
    "Status",
  ];
  const outstandingHeaders = [
    "Merchant ID",
    "Merchant Name",
    "Invoice ID",
    "Amount",
    "Crypto Wallet Used",
    "Recurring Period",
    "Outstanding Amount",
    "Start Date",
    "Next Billing Cycle Date",
    "Status",
    "Actions",
  ];
  const requestedHeaders = [
    "Merchant ID",
    "Merchant Name",
    "Merchant Email",
    "Invoice ID",
    "Amount",
    "Crypto Wallet Used",
    "Recurring Period",
    "Start Date",
    "Next Billing Cycle Date",
    "Number of Billing Cycle",
    "Status",
    "Actions",
  ];

  return (
    <Box sx={pageWrapper}>
      {/* Hero Banner */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(
            btncolor || "#1976d2",
            0.95
          )} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
        }}
      >
        <Box sx={heroContent}>
          <Box sx={heroIconBadge}>
            <AutorenewOutlinedIcon sx={{ color: "#fff", fontSize: 30 }} />
          </Box>
          <Box>
            <Typography sx={heroTitle}>Recurring Payments</Typography>
            <Typography sx={heroSubtitle}>
              Manage your active subscriptions, outstanding payments and
              merchant-requested recurring billing
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Card */}
      <Box sx={mainCard}>
        {/* ── How it works — always visible, outside tabs ── */}
        <Box
          sx={{
            ...howItWorksWrap,
            px: { xs: 1.5, md: 2 },
            pt: { xs: 1.5, md: 2 },
          }}
        >
          {/* Header row */}
          <Box sx={hiw_headerRow}>
            <Box
              sx={{
                ...hiw_headerAccent,
                background: `linear-gradient(180deg, ${alpha(
                  btncolor || "#1976d2",
                  1
                )} 0%, ${alpha(btncolor || "#1976d2", 0.4)} 100%)`,
              }}
            />
            <Box>
              <Typography sx={hiw_headerTitle}>How it works</Typography>
              <Typography sx={hiw_headerSub}>
                Follow these steps to set up recurring payments in crypto with a
                new merchant.
              </Typography>
            </Box>
          </Box>

          {/* Timeline steps */}
          {[
            {
              icon: <StorefrontOutlinedIcon sx={{ fontSize: 16 }} />,
              label: "Step 1",
              title: "Create merchant account",
              desc: (
                <>
                  Ask your merchant to sign up for a merchant account.{" "}
                  <a
                    href={`${window.location.origin}/payments/signup`}
                    target="_blank"
                    style={{
                      color: "var(--text-color)",
                      fontWeight: 600,
                      textDecoration: "underline",
                      textUnderlineOffset: "3px",
                      textDecorationThickness: "1.5px",
                      padding: "1px 3px",
                      borderRadius: "3px",
                    }}
                  >
                    Sign up here.
                  </a>
                </>
              ),
            },
            {
              icon: <HowToRegOutlinedIcon sx={{ fontSize: 16 }} />,
              label: "Step 2",
              title: "Create subscription",
              desc: (
                <>
                  Your merchant logs in their merchant account and creates a
                  subscription payment.{" "}
                  <a
                    href={`${window.location.origin}/payments/create-payment`}
                    target="_blank"
                    style={{
                      color: "var(--text-color)",
                      fontWeight: 600,
                      textDecoration: "underline",
                      textUnderlineOffset: "3px",
                      textDecorationThickness: "1.5px",
                      padding: "1px 3px",
                      borderRadius: "3px",
                    }}
                  >
                    Create subscription payment here.
                  </a>
                </>
              ),
            },
            {
              icon: <TaskAltOutlinedIcon sx={{ fontSize: 16 }} />,
              label: "Step 3",
              title: "Email subscription payment link",
              desc: "Your merchant emails the subscription payment link to your email.",
            },
            {
              icon: <LanguageOutlinedIcon sx={{ fontSize: 16 }} />,
              label: "Step 4",
              title:
                "Use the checkout button in your email to subscribe and pay.",
              desc: (
                <>
                  Once you receive the email from your merchant, open the email
                  and click on the{" "}
                  <strong style={{ color: "var(--text-color)" }}>
                    Complete Payment Now
                  </strong>{" "}
                  button to subscribe and pay to the merchant.
                </>
              ),
            },
          ].map((step, i, arr) => (
            <Box key={i} sx={hiw_timelineRow}>
              {/* Left column: connector line + node */}
              <Box sx={hiw_connectorCol}>
                <Box
                  sx={{
                    ...hiw_node,
                    backgroundColor: btncolor,
                    color: "#fff",
                  }}
                >
                  {step.icon}
                </Box>
                {i < arr.length - 1 && (
                  <Box
                    sx={{
                      ...hiw_line,
                      borderColor: alpha(btncolor || "#1976d2", 0.35),
                      borderStyle: "solid",
                    }}
                  />
                )}
              </Box>

              {/* Right column: content */}
              <Box sx={{ ...hiw_contentBox, mb: i < arr.length - 1 ? 0 : 0 }}>
                <Typography sx={hiw_stepLabel}>{step.label}</Typography>
                <Typography sx={hiw_stepTitle}>{step.title}</Typography>
                <Typography sx={hiw_stepDesc}>{step.desc}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Main Tabs */}
        <Box sx={tabsWrap}>
          <Tabs
            value={mainTab}
            onChange={(e, v) => setMainTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              ...tabsStyle,
              "& .MuiTabs-indicator": {
                backgroundColor: btncolor,
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            {mainTabsMeta.map((t, i) => (
              <Tab
                key={i}
                iconPosition="start"
                icon={t.icon}
                label={t.label}
                sx={tabItem}
              />
            ))}
          </Tabs>
        </Box>

        {/* TAB 0 — Enable and Manage */}
        {mainTab === 0 && (
          <Box sx={tabPanel}>
            {/* Sub Tabs */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid rgba(125,125,125,0.13)",
                mb: 2,
              }}
            >
              <Tabs
                value={subTab}
                onChange={(e, v) => setSubTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  ...subTabsStyle,
                  "& .MuiTabs-indicator": {
                    backgroundColor: btncolor,
                    height: 2,
                    borderRadius: "2px 2px 0 0",
                  },
                }}
              >
                {subTabsMeta.map((t, i) => (
                  <Tab key={i} label={t.label} sx={subTabItem} />
                ))}
              </Tabs>
              <Tooltip title="Reload data">
                <IconButton
                  onClick={() => getRecurringPaymentDetails()}
                  size="small"
                  sx={{ color: "success.main", mr: 0.5 }}
                >
                  <ReplayIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Sub Tab Panels */}
            {subTab === 0 && activeSubscriptionDisplay && (
              <TableContainer component={Paper} sx={modernTableContainer}>
                <Table sx={tableStyle}>
                  <TableHead>
                    <TableRow sx={modernTableHeadRow}>
                      {activeHeaders.map((h, i) => (
                        <TableCell key={i} sx={modernTableHeadCell}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeSubscriptionsList.length > 0
                      ? activeSubscriptionsList.map((payment, index) => (
                          <TableRow key={index} sx={modernTableBodyRow}>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.merchantId}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.merchantName}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.invoiceId}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.amount} USD
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.cryptoWalletUsed}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.recurringPeriod}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.outstandingAmount} {payment.currencyCode}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.startDate}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.nextBillingCycleDate}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.status}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              <Tooltip title="Stop Recurring Payment">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() =>
                                    updateRecurringPayment(payment.invoiceId, 2)
                                  }
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      : renderEmptyRow(11, "No Active Subscriptions Found")}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {subTab === 1 && canceledSubscriptionDisplay && (
              <TableContainer component={Paper} sx={modernTableContainer}>
                <Table sx={tableStyle}>
                  <TableHead>
                    <TableRow sx={modernTableHeadRow}>
                      {cancelledHeaders.map((h, i) => (
                        <TableCell key={i} sx={modernTableHeadCell}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {canceledSubscriptionsList.length > 0
                      ? canceledSubscriptionsList.map((payment, index) => (
                          <TableRow key={index} sx={modernTableBodyRow}>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.merchantId}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.merchantName}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.invoiceId}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.amount} USD
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.cryptoWalletUsed}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.recurringPeriod}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.outstandingAmount} {payment.currencyCode}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.startDate}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.endDate}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.status}
                            </TableCell>
                          </TableRow>
                        ))
                      : renderEmptyRow(10, "No Cancelled Subscriptions Found")}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {subTab === 2 && outstandingSubscriptionsDisplay && (
              <TableContainer component={Paper} sx={modernTableContainer}>
                <Table sx={tableStyle}>
                  <TableHead>
                    <TableRow sx={modernTableHeadRow}>
                      {outstandingHeaders.map((h, i) => (
                        <TableCell key={i} sx={modernTableHeadCell}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {outstandingSubscriptionsList.length > 0
                      ? outstandingSubscriptionsList.map((payment, index) => (
                          <TableRow key={index} sx={modernTableBodyRow}>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.merchantId}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.merchantName}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.invoiceId}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.amount} USD
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.cryptoWalletUsed}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.recurringPeriod}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.outstandingAmount} {payment.currencyCode}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.startDate}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.nextBillingCycleDate}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.status}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              <Tooltip title="Stop Recurring Payment">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() =>
                                    updateRecurringPayment(payment.invoiceId, 2)
                                  }
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      : renderEmptyRow(
                          11,
                          "No Outstanding Subscriptions Found"
                        )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* TAB 1 — View Requested */}
        {mainTab === 1 && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {requestedSubscriptionDisplay && (
              <TableContainer component={Paper} sx={modernTableContainer}>
                <Table sx={tableStyle}>
                  <TableHead>
                    <TableRow sx={modernTableHeadRow}>
                      {requestedHeaders.map((h, i) => (
                        <TableCell key={i} sx={modernTableHeadCell}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requestedSubscriptionsList.length > 0
                      ? requestedSubscriptionsList.map((payment, index) => (
                          <TableRow key={index} sx={modernTableBodyRow}>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.merchantId}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.merchantName}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.merchantEmail}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.invoiceId}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.amount} USD
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.cryptoWalletUsed}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.recurringPeriod}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.startDate}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.nextBillingCycleDate}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.totalCycles}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              {payment.status}
                            </TableCell>
                            <TableCell sx={modernTableBodyCell}>
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                <Tooltip title="Enable Recurring Payment">
                                  <IconButton
                                    color="primary"
                                    size="small"
                                    onClick={() =>
                                      updateRecurringPayment(
                                        payment.invoiceId,
                                        1
                                      )
                                    }
                                  >
                                    <ReplyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Decline Recurring Payment">
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() =>
                                      updateRecurringPayment(
                                        payment.invoiceId,
                                        2
                                      )
                                    }
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      : renderEmptyRow(12, "No Requested Subscriptions Found")}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Styles (mirrors Report.js conventions) ────────────────────────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const heroBanner = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  position: "relative",
  zIndex: 1,
};

const heroIconBadge = {
  width: 56,
  height: 56,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
};

const heroTitle = {
  fontSize: { xs: "1.4rem", md: "1.7rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.015em",
};

const heroSubtitle = {
  fontSize: { xs: "0.82rem", md: "0.9rem" },
  color: "rgba(255,255,255,0.88)",
  mt: 0.5,
  maxWidth: 640,
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const tabsWrap = {
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  px: { xs: 1, md: 2 },
  backgroundColor: "var(--body-color-less2)",
};

const tabsStyle = {
  minHeight: 56,
  "& .MuiTab-root": { minHeight: 56 },
};

const tabItem = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.9rem",
  color: "var(--text-color)",
  opacity: 0.65,
  letterSpacing: "0.01em",
  px: 2,
  gap: 0.75,
  "&.Mui-selected": { color: "var(--text-color)", opacity: 1 },
};

// ── How it works — timeline styles ──────────────────────────────────────────

const howItWorksWrap = {
  mb: 2,
};

const hiw_headerRow = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.5,
  mb: 2,
  pb: 1.5,
  borderBottom: "1px solid rgba(125,125,125,0.13)",
};

const hiw_headerAccent = {
  width: 4,
  borderRadius: "4px",
  alignSelf: "stretch",
  minHeight: 36,
  flexShrink: 0,
};

const hiw_headerTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.3,
  mb: 0.3,
};

const hiw_headerSub = {
  fontSize: "0.8rem",
  color: "var(--text-color)",
  opacity: 0.55,
  lineHeight: 1.5,
};

// Timeline row
const hiw_timelineRow = {
  display: "flex",
  alignItems: "stretch",
  gap: 2,
};

const hiw_connectorCol = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flexShrink: 0,
  width: 32,
};

const hiw_node = {
  width: 32,
  height: 32,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  zIndex: 1,
};

const hiw_line = {
  width: 0,
  flex: 1,
  minHeight: 12,
  borderLeft: "2px solid",
  mt: "3px",
  mb: "3px",
};

const hiw_contentBox = {
  flex: 1,
  pb: 1.5,
};

const hiw_stepLabel = {
  fontSize: "0.68rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.09em",
  mb: 0.3,
  lineHeight: 1,
  color: "var(--text-color)",
  opacity: 0.45,
};

const hiw_stepTitle = {
  fontSize: "0.9rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.3,
  mb: 0.6,
};

const hiw_stepDesc = {
  fontSize: "0.82rem",
  color: "var(--text-color)",
  opacity: 0.65,
  lineHeight: 1.65,
};

const tabPanel = {
  p: { xs: 1.5, md: 2 },
};

const subTabsStyle = {
  minHeight: 46,
  "& .MuiTab-root": { minHeight: 46 },
};

const subTabItem = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.82rem",
  color: "var(--text-color)",
  opacity: 0.65,
  letterSpacing: "0.01em",
  px: 2,
  "&.Mui-selected": { color: "var(--text-color)", opacity: 1 },
};

// Tables
const modernTableContainer = {
  borderRadius: "12px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "none",
  overflowX: "auto",
  backgroundColor: "var(--body-color-less)",
};

const tableStyle = {
  minWidth: 900,
};

const modernTableHeadRow = {
  backgroundColor: "var(--body-color-less2)",
};

const modernTableHeadCell = {
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.75,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  py: 1.75,
  whiteSpace: "nowrap",
};

const modernTableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "var(--body-color-less2)" },
  "&:last-child td": { borderBottom: 0 },
};

const modernTableBodyCell = {
  fontSize: "0.85rem",
  py: 1.65,
  borderBottom: "1px solid rgba(125,125,125,0.12)",
  color: "var(--text-color)",
  whiteSpace: "nowrap",
};

const emptyCell = {
  textAlign: "center",
  py: 7,
  borderBottom: 0,
};

const emptyIcon = {
  fontSize: 48,
  color: "var(--text-color)",
  opacity: 0.35,
  mb: 1,
};

const emptyText = {
  fontSize: "0.92rem",
  color: "var(--text-color)",
  opacity: 0.65,
};

export default RecurringPayments;
